// Anmeldeseite: E-Mail oder Portal-Login + Passwort (Zugang wie im Mitarbeiterportal), „Angemeldet bleiben“.
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { ApiFehler } from "../api";
import { useAuth } from "../auth/AuthContext";
import { fehlerInfo } from "../components/Fehler";
import { EinladungAnnehmen } from "../nutzer/EinladungAnnehmen";
import { T8C } from "../nutzer/texte";
import { T } from "../texte";
import "./einstellungen/nutzer.css";

export default function Login() {
  const { nutzer, anmelden } = useAuth();
  const navigate = useNavigate();
  const ort = useLocation();
  const ziel = (ort.state as { von?: string } | null)?.von || "/inbox";
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [bleiben, setBleiben] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  // Zwei-Faktor (Runde 8, Agent 8C): hat der Nutzer es, fragt der Server nach dem Passwort nach dem Code
  const [codeSchritt, setCodeSchritt] = useState(false);
  const [code, setCode] = useState("");
  // Einladungslink aus Einstellungen › Nutzer: /login?einladung=<Token>
  const einladung = new URLSearchParams(ort.search).get("einladung");

  if (einladung) return <EinladungAnnehmen token={einladung} />;
  if (nutzer) return <Navigate to={ziel === "/login" ? "/inbox" : ziel} replace />;

  const absenden = async (e: FormEvent) => {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      await anmelden({ email: email.trim(), password: passwort, remember: bleiben, ...(codeSchritt ? { code: code.trim() } : {}) });
      navigate(ziel === "/login" ? "/inbox" : ziel, { replace: true });
    } catch (err) {
      if (err instanceof ApiFehler && err.code === "zwei_faktor_noetig") {
        setCodeSchritt(true);
        setCode("");
      } else if (codeSchritt && err instanceof ApiFehler && err.code === "zwei_faktor_falsch") {
        setFehler(fehlerInfo(err).text);
        setCode("");
      } else {
        setFehler(fehlerInfo(err).text);
        setPasswort("");
        setCodeSchritt(false);
        setCode("");
      }
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="anmeldung">
      <form className="anmeldung__karte" onSubmit={absenden}>
        <div className="anmeldung__marke">
          <img className="anmeldung__logo" src={logo} alt="B2B Förderzentrum" width={210} height={47} />
          <span className="anmeldung__untertitel">CRM</span>
        </div>
        <h1>{T.login.titel}</h1>
        {fehler ? (
          <div className="hinweis hinweis--fehler" role="alert">
            {fehler}
          </div>
        ) : null}
        {codeSchritt ? (
          <>
            <p className="seite__text">{T8C.login.codeText}</p>
            <label className="feld">
              <span className="feld__label">{T8C.login.code}</span>
              <input
                className="eingabe anmeldung__code"
                autoComplete="one-time-code"
                inputMode="text"
                maxLength={20}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />
            </label>
            <p className="nv-hilfe">{T8C.login.codeHilfe}</p>
            <button className="knopf knopf--primaer knopf--breit" type="submit" disabled={laeuft || !code.trim()}>
              {laeuft ? T.login.laeuft : T8C.login.weiter}
            </button>
            <div className="anmeldung__links">
              <button
                type="button"
                className="nv-linkknopf"
                onClick={() => {
                  setCodeSchritt(false);
                  setCode("");
                  setFehler(null);
                }}
              >
                {T8C.login.zurueck}
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="feld">
              <span className="feld__label">{T.login.email}</span>
              <input className="eingabe" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </label>
            <label className="feld">
              <span className="feld__label">{T.login.passwort}</span>
              <input className="eingabe" type="password" autoComplete="current-password" value={passwort} onChange={(e) => setPasswort(e.target.value)} required />
            </label>
            <label className="anmeldung__haken">
              <input type="checkbox" checked={bleiben} onChange={(e) => setBleiben(e.target.checked)} />
              {T.login.bleiben}
            </label>
            <button className="knopf knopf--primaer knopf--breit" type="submit" disabled={laeuft}>
              {laeuft ? T.login.laeuft : T.login.knopf}
            </button>
            <p className="seite__text">{T.login.hinweis}</p>
          </>
        )}
      </form>
    </div>
  );
}
